import { useEffect, useState } from "react";

import { kaiApi } from "@/services/kaiApi";
import { KaiScheduledReportsSnapshot } from "./KaiScheduledReportsSnapshot";
import { parseScheduledReportsSnapshotSpec } from "./kaiScheduledReportsTypes";
import { KaiBlockLoading } from "./KaiBlockLoading";
import { KaiBlockError } from "./KaiBlockError";

type Spec = NonNullable<ReturnType<typeof parseScheduledReportsSnapshotSpec>>;

/**
 * Resolves a DB-backed `scheduled_reports_snapshot` artifact by ID. During a
 * live turn the SSE payload may include the full card under `artifact`; on
 * reload we fetch it.
 */
export function KaiScheduledReportsSnapshotRef({
  artifactId,
  conversationId,
  cachedArtifact,
  beforeNavigate,
}: {
  artifactId: string;
  conversationId?: string | null;
  cachedArtifact?: unknown;
  beforeNavigate?: () => void;
}) {
  const cachedSpec = cachedArtifact
    ? parseScheduledReportsSnapshotSpec(JSON.stringify(cachedArtifact))
    : null;

  const [spec, setSpec] = useState<Spec | null>(cachedSpec);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (spec || !conversationId || !artifactId) return;
    let cancelled = false;
    (async () => {
      try {
        const artifact = await kaiApi.getArtifact(conversationId, artifactId);
        const parsed = parseScheduledReportsSnapshotSpec(
          JSON.stringify(artifact.payload),
        );
        if (cancelled) return;
        if (parsed) setSpec(parsed);
        else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spec, conversationId, artifactId]);

  if (spec)
    return (
      <KaiScheduledReportsSnapshot spec={spec} beforeNavigate={beforeNavigate} />
    );
  if (failed) return <KaiBlockError tag="scheduled_reports_snapshot" />;
  return <KaiBlockLoading label="Loading scheduled reports…" />;
}
